import { Loading01 } from '@aktiia/design-system';
import cx from 'classnames';
import { MouseEvent, forwardRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { IButtonProps } from './types';

const Button = forwardRef<HTMLButtonElement, IButtonProps>(
  (
    {
      text,
      onClick,
      className,
      path,
      leftIcon,
      rightIcon,
      loading = false,
      type = 'primary',
      size = 'md',
      disabled = false,
      selected = false,
    }: IButtonProps,
    ref,
  ) => {
    const navigate = useNavigate();
    const buttonClassName = useMemo(() => {
      return cx(className, 'flex items-center', {
        'bg-background-button-primary text-text-primaryInverse enabled:hover:bg-background-button-primaryHover':
          type === 'primary' || (type === 'tertiary' && selected),
        'bg-global-transparent-black-10 text-textPrimary border border-border-strong enabled:hover:bg-background-button-secondaryHover':
          type === 'secondary' && !selected,
        'bg-background-surface-dark text-text-primaryInverse': type === 'secondary' && selected,
        'bg-global-transparent-black-10 text-textPrimary border border-border-light enabled:hover:bg-background-button-tertiaryHover':
          type === 'tertiary' && !selected,
        'text-text-link enabled:hover:bg-background-button-plainHover': type === 'plain',
        'bg-background-button-negative text-text-primaryInverse enabled:hover:bg-background-button-primaryHover':
          type === 'destructive',
        'h-[56px] typography-button-label-large px-6 rounded-lg': size === 'lg',
        'h-[48px] typography-button-label-2 px-4 rounded-lg': size === 'md',
        'h-[32px] typography-button-label-3 px-3 rounded-full': size === 'sm',
        'opacity-50': disabled,
      });
    }, [className, type, size, disabled, selected]);
    const leftIconClassName = useMemo(() => {
      return cx({ 'mr-3': size === 'lg', 'mr-2': size === 'sm' || size === 'md' });
    }, [size]);
    const rightIconClassName = useMemo(() => {
      return cx({ 'ml-3': size === 'lg', 'ml-2': size === 'sm' || size === 'md' });
    }, [size]);

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
      if (path) {
        navigate(path);
      }
      if (onClick) {
        onClick(event);
      }
    };

    return (
      <button ref={ref} className={buttonClassName} onClick={handleClick} disabled={disabled}>
        {loading ? (
          <span>
            <Loading01 className="animate-spin" />
          </span>
        ) : (
          <>
            {leftIcon && <span className={leftIconClassName}>{leftIcon}</span>}

            {text}

            {rightIcon && <span className={rightIconClassName}>{rightIcon}</span>}
          </>
        )}
      </button>
    );
  },
);
Button.displayName = 'Button';

export { Button };
